<script setup>
/**
 * @description Product list card component as per design image
 * @param {Object} product - Product data object
 * @param {string} product.id - Product ID
 * @param {string} product.name - Product model (e.g., G118P-C)
 * @param {string} product.description - Product title (e.g., 18口千兆监控PoE交换机)
 * @param {string} product.subtitle - Feature highlights (optional)
 * @param {string} product.image - Product image URL
 * @param {number} product.price - Current price
 * @param {number} product.originalPrice - Original price
 * @param {Array} [product.tags] - Array of tags (e.g., ['秒杀'])
 * @param {string} [product.tag] - Single tag (fallback)
 * @param {number} [subtitleLine=2] - Number of visible lines for subtitle text
 * @emits click - Emits when card is clicked
 * @slot price-action - Custom action area on the right side of the price row
 * @example
 * <ProductListCard :product="productData" @click="handleProductClick" />
 */

// 1. Imports
// BaseImage is auto-imported
import { debounce, formatCompactPriceText, getOssUrl, splitCompactPriceParts } from '@/utils'
import {
  isPromotionActivityNotStarted,
  isPromotionListQuickAddType,
  normalizePromotionTierPriceTagLabel,
  resolveActivityCountdown,
  resolveCampaignTopDisplayText,
  resolveCampaignTopTone,
  resolvePromotionTierPriceTagLabel,
} from '@/utils/store/promotionDisplay'
import { resolveBuyGiftLandingPromoTagLabel } from '@/utils/store/buyGiftPromotion'
import { PROMOTION_TYPE } from '@/utils/store/promotionTypes'
import {
  PRODUCT_IDENTITY_TAGS,
  buildListAddToCartPayload,
  normalizeProductData,
  resolveProductActTags,
} from '@/utils/product'
import { BaseMessage } from '~/utils/baseMessage'
import { IconUp } from '@arco-design/web-vue/es/icon'
import { computed, onMounted, ref, watch } from 'vue'
import BaseImage from '~/components/base/Image.vue'
import BaseOverflowTooltip from '~/components/base/OverflowTooltip.vue'
import ProductImageTags from '~/components/store/common/ProductImageTags.vue'
import { useGlobalTimer } from '~/composables/useGlobalTimer'
import { useVideoPlayer } from '~/composables/useVideoPlayer'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { useProductData } from '~/composables/store/useProductData'
import { useUserStore } from '~/store/common/user'
import { useStoreCartStore } from '~/store/store/cart'
import { useStoreCompareStore } from '~/store/store/compare'
import { useCardSelection } from '~/composables/useCardSelection'
import { PRODUCT_DELISTED_FLAG, PRODUCT_STATUS } from '~/utils/constant'
import { resolveProductInventory } from '~/utils/store/inventoryAvailability'
import { trackClickEvent } from '~/composables/usePageTracking'
import { buildAddToCartTrack, buildProductClickPayload } from '~/utils/tracking/eventBuilders'

// 2. Props
const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  background: {
    type: String,
    default: '',
  },
  showCompare: {
    type: Boolean,
    default: false,
  },
  showCart: {
    type: Boolean,
    default: true,
  },
  showCampaignInfo: {
    type: Boolean,
    default: false,
  },
  showActivityTag: {
    type: Boolean,
    default: true,
  },
  subtitleLine: {
    type: Number,
    default: 2,
  },
  tagLimit: {
    type: Number,
    default: 0,
  },
  trackContext: {
    type: Object,
    default: null,
  },
})

// 3. Emits
// No emits needed as we use NuxtLink for navigation

// 4. Refs/Reactive State
const compareStore = useStoreCompareStore()
const cartStore = useStoreCartStore()
const userStore = useUserStore()
const { isMobile } = useMediaQuery()
const shouldHideProductCardCart = computed(() => userStore.shouldHideProductCardCart)
const { now } = useGlobalTimer()

onMounted(() => {
  userStore.ensureStoreAgencyIdentity()
})
const {
  syncVideoBindings,
  handleVideoPlay,
  handleVideoPause,
  handleVideoSeeking,
  handleVideoSeeked,
  handleVideoEnded,
} = useVideoPlayer()

const {
  productId,
  modelNo,
  name,
  description,
  image,
  displayPrice,
  originalPrice,
  tags,
  hasTags,
  detailTo,
} = useProductData(props.product)

const { handleCardClick } = useCardSelection()

const isInCompare = computed(() => compareStore.isInCompare(productId.value))
const isFull = computed(() => compareStore.isFull)
const isDisabled = computed(() => !isInCompare.value && isFull.value)
const isOffShelf = computed(() => props.product?.status === PRODUCT_STATUS.OFF_SHELF)
const isDelisted = computed(
  () => isOffShelf.value && props.product?.isDelisted === PRODUCT_DELISTED_FLAG.DELISTED
)
const isAgencyIdentityPending = computed(() => userStore.isLoggedIn && !userStore.hasAgencyIdentity)
const inventory = computed(() =>
  resolveProductInventory(props.product, null, { isAgencyUser: userStore.isAgencyUser })
)
const isSoldOut = computed(
  () => !isOffShelf.value && !isAgencyIdentityPending.value && inventory.value.soldOut
)
const isInventoryUpdating = computed(
  () => !isOffShelf.value && !isAgencyIdentityPending.value && inventory.value.updating
)
const isUnavailable = computed(
  () => isOffShelf.value || isSoldOut.value || isInventoryUpdating.value
)
const canViewDetail = computed(() => !isOffShelf.value || isDelisted.value)
const unavailableLabel = computed(() => {
  if (isDelisted.value) return '已退市'
  if (isInventoryUpdating.value) return '库存更新中'
  if (isSoldOut.value) return '售罄'
  return '已下架'
})
const visibleTags = computed(() => {
  const identityTags = tags.value.filter((tag) => PRODUCT_IDENTITY_TAGS.includes(tag))
  if (props.tagLimit > 0) {
    return identityTags.slice(0, props.tagLimit)
  }
  return identityTags
})
const activityTags = computed(() => resolveProductActTags(props.product))
const hasActivityTag = computed(() => props.showActivityTag && activityTags.value.length > 0)
const countdownText = computed(() =>
  props.showCampaignInfo
    ? String(
        props.product?.flashCountdownText ??
          props.product?.countdownText ??
          props.product?.seckillCountdownText ??
          ''
      ).trim()
    : ''
)
const liveActivityCountdown = computed(() => {
  if (!props.showCampaignInfo) return null

  const startTime = props.product?.activityStartTime ?? props.product?.startTime
  const endTime = props.product?.activityEndTime ?? props.product?.endTime
  if (!startTime && !endTime) return null

  void now.value
  return resolveActivityCountdown(
    {
      startTime,
      endTime,
      activityStatus: props.product?.activityStatus,
      displayStatus: props.product?.displayStatus,
    },
    now.value
  )
})
const campaignTopText = computed(() => {
  if (!props.showCampaignInfo) return ''

  const startTime = props.product?.activityStartTime ?? props.product?.startTime
  const endTime = props.product?.activityEndTime ?? props.product?.endTime

  return resolveCampaignTopDisplayText({
    liveCountdown: liveActivityCountdown.value,
    staticText: countdownText.value,
    countdownType: props.product?.campaignCountdownType,
    startTime,
    endTime,
    directText: props.product?.campaignTopText ?? props.product?.flashStartText,
  })
})
const campaignTopTone = computed(() => {
  const fallbackTone = props.product?.campaignTopTone === 'warning' ? 'warning' : 'danger'
  if (liveActivityCountdown.value) {
    return resolveCampaignTopTone(liveActivityCountdown.value, fallbackTone)
  }
  return fallbackTone
})
const campaignActionLabel = computed(() => {
  if (!props.showCampaignInfo) return ''
  if (isCampaignNotStarted.value) return '未开始'
  return String(props.product?.campaignActionLabel ?? '').trim()
})
const isCampaignNotStarted = computed(() => {
  if (!props.showCampaignInfo) return false
  if (!isPromotionListQuickAddType(Number(props.product?.promotionType))) return false

  void now.value
  return isPromotionActivityNotStarted(
    {
      startTime: props.product?.activityStartTime ?? props.product?.startTime,
      endTime: props.product?.activityEndTime ?? props.product?.endTime,
      activityStatus: props.product?.activityStatus,
      displayStatus: props.product?.displayStatus,
    },
    now.value
  )
})
const effectiveShowCart = computed(
  () => props.showCart && !isCampaignNotStarted.value && !shouldHideProductCardCart.value
)
const allTierPriceTags = computed(() => {
  if (!props.showCampaignInfo) return []
  const activityType = Number(props.product?.promotionType)
  const isBuyGift = activityType === PROMOTION_TYPE.BUY_GIFT
  const discountCalc = props.product?.discountCalc
  const unit = props.product?.prodUnit || '台'
  const raw =
    props.product?.tierPriceTags ??
    props.product?.discountTierTags ??
    props.product?.ladderPriceTags ??
    []
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (typeof item === 'string') {
        const label = isBuyGift
          ? resolveBuyGiftLandingPromoTagLabel({ displayText: item }, { discountCalc, unit })
          : normalizePromotionTierPriceTagLabel(item, unit)
        return label ? { label } : null
      }
      if (!item || typeof item !== 'object') return null
      if (isBuyGift) {
        const prebuilt = String(item.label || '').trim()
        if (prebuilt) return { label: prebuilt }
        const label = resolveBuyGiftLandingPromoTagLabel(
          { ...item, buyNum: item.buyNum ?? item.threshold },
          { discountCalc, unit }
        )
        return label ? { label } : null
      }
      const label = resolvePromotionTierPriceTagLabel(item, activityType, unit)
      return label ? { label } : null
    })
    .filter(Boolean)
})
const tierPriceVisibleLimit = computed(() => {
  if (!props.showCampaignInfo) return 2
  if (isMobile.value) {
    return 2
  }
  return 2
})
const isTierPriceExpanded = ref(false)
const visibleTierPriceTags = computed(() =>
  isTierPriceExpanded.value
    ? allTierPriceTags.value
    : allTierPriceTags.value.slice(0, tierPriceVisibleLimit.value)
)
const hiddenTierPriceTags = computed(() =>
  isTierPriceExpanded.value ? [] : allTierPriceTags.value.slice(tierPriceVisibleLimit.value)
)
const tierPriceOverflowCount = computed(() => {
  if (!props.showCampaignInfo) return 0
  const explicit = Number(props.product?.tierPriceOverflowCount)
  if (Number.isFinite(explicit) && explicit > 0) return Math.trunc(explicit)
  return Math.max(0, allTierPriceTags.value.length - tierPriceVisibleLimit.value)
})
const hasTierPriceTags = computed(
  () => props.showCampaignInfo && visibleTierPriceTags.value.length > 0
)
const canCollapseTierPriceTags = computed(
  () => isTierPriceExpanded.value && allTierPriceTags.value.length > tierPriceVisibleLimit.value
)
const tierPopoverTrigger = computed(() => (isMobile.value ? 'click' : 'hover'))
const shouldHideBriefInCard = computed(() => props.showCampaignInfo)

const campaignPriceDisplay = computed(() => {
  if (!props.showCampaignInfo) return null
  const display = props.product?.campaignPriceDisplay
  if (!display || typeof display !== 'object') return null
  if (!String(display.price ?? '').trim()) return null
  return display
})

const campaignPriceParts = computed(() => splitCompactPriceParts(campaignPriceDisplay.value?.price))

const effectiveDisplayPrice = computed(() => {
  if (campaignPriceDisplay.value) return ''
  const override = props.showCampaignInfo ? props.product?.cardDisplayPrice : undefined
  return override !== undefined && override !== null && override !== ''
    ? override
    : displayPrice.value
})

const displayPriceParts = computed(() => splitCompactPriceParts(effectiveDisplayPrice.value))

const originalPriceParts = computed(() => splitCompactPriceParts(originalPrice.value))
const compactEffectiveDisplayPrice = computed(() =>
  formatCompactPriceText(effectiveDisplayPrice.value)
)
const compactOriginalPrice = computed(() => formatCompactPriceText(originalPrice.value))

/**
 * @description 解析价格字符串为数字（兼容千分位）
 * @param {string|number|null|undefined} value
 * @returns {number}
 */
const parsePriceNumber = (value) => {
  const normalized = String(value ?? '')
    .trim()
    .replace(/,/g, '')
  const num = Number(normalized)
  return Number.isFinite(num) ? num : NaN
}

const shouldShowOriginalPrice = computed(() => {
  const orig = parsePriceNumber(originalPrice.value)
  const curr = parsePriceNumber(effectiveDisplayPrice.value)
  return Number.isFinite(orig) && orig > 0 && Number.isFinite(curr) && orig > curr
})
const marketingMediaUrl = computed(() => String(props.product?.marketingMedia || '').trim())
const isMarketingVideo = computed(
  () =>
    String(props.product?.mediaType || '').toUpperCase() === 'VIDEO' &&
    Boolean(marketingMediaUrl.value)
)
const resolvedMarketingVideoUrl = computed(() => getOssUrl(marketingMediaUrl.value))

const setMarketingVideoRef = (el) => {
  syncVideoBindings(el ? [el] : [])
}

watch(
  () => [productId.value, allTierPriceTags.value.map((item) => item.label).join('|')],
  () => {
    isTierPriceExpanded.value = false
  }
)

// 5. Methods
/**
 * @description 加入购物车逻辑
 * @reason 参考 gcb-xcx 实现，增加防抖处理，逻辑同步至 store/cart.js
 */
const handleAddToCart = debounce(() => {
  if (userStore.guardStoreOrderOrNotify(isMobile.value)) return
  if (isCampaignNotStarted.value) {
    BaseMessage.warning('活动尚未开始')
    return
  }
  if (isUnavailable.value) {
    BaseMessage.warning(
      inventory.value.blocked ? inventory.value.message : '商品已下架，无法加入购物车'
    )
    return
  }
  cartStore.addToCart({
    ...buildListAddToCartPayload(normalizeProductData(props.product)),
    selectAvailableSku: true,
    ...(props.trackContext?.addToCartEvent
      ? {
          track: buildAddToCartTrack({
            eventName: props.trackContext.addToCartEvent,
            pageName: props.trackContext.pageName || '商城',
            fromPage: props.trackContext.fromPage || '',
            product: normalizeProductData(props.product),
            trackContext: props.trackContext,
          }),
        }
      : {}),
  })
}, 100)

const handleCompareToggle = () => {
  if (isDisabled.value) return
  compareStore.toggleCompare(props.product)
}

const expandTierPriceTags = () => {
  if (tierPriceOverflowCount.value <= 0) return
  isTierPriceExpanded.value = true
}

const collapseTierPriceTags = () => {
  isTierPriceExpanded.value = false
}

/**
 * @description 控制商品卡片是否允许进入商详
 * @reason 已下架且已退市的商品允许查看商详；仅下架未退市的商品禁止查看
 * @param {MouseEvent} e - 卡片点击事件
 * @returns {void}
 */
const onCardClick = (e) => {
  if (!canViewDetail.value) {
    e.preventDefault()
    e.stopPropagation()
    BaseMessage.warning('商品已下架，无法查看')
    return
  }
  handleCardClick(e)
  if (props.trackContext?.clickEvent) {
    const product = normalizeProductData(props.product)
    trackClickEvent(
      buildProductClickPayload({
        pvEvent: props.trackContext.clickEvent,
        pageName: props.trackContext.pageName || '商城',
        product,
        trackContext: props.trackContext,
        extra: {
          ...(props.trackContext.extra || {}),
          ...(product?.activityId || product?.promotionId
            ? {
                activity_id: String(product.activityId ?? product.promotionId ?? ''),
                activity_name: String(product.activityName || product.promotionName || ''),
              }
            : {}),
        },
      })
    )
  }
}
</script>

<template>
  <NuxtLink
    class="product-list-card"
    :data-prod-id="product.prodId || product.id || ''"
    :class="{
      'product-list-card--campaign': props.showCampaignInfo,
      'product-list-card--tier-expanded': isTierPriceExpanded,
    }"
    :to="detailTo"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="查看商品详情"
    :style="props.background ? { backgroundColor: props.background } : undefined"
    draggable="false"
    @click="onCardClick"
  >
    <div
      v-if="campaignTopText"
      class="product-list-card__countdown"
      :class="{ 'product-list-card__countdown--warning': campaignTopTone === 'warning' }"
    >
      {{ campaignTopText }}
    </div>
    <div class="product-list-card__inner">
      <div class="product-list-card__media">
        <!-- Tags (Top Left) -->
        <ProductImageTags class="product-list-card__tags" :tags="visibleTags" reserve-space />

        <!-- Actions Slot (Top Right) -->
        <div class="product-list-card__actions">
          <slot name="actions" />
        </div>

        <div class="product-list-card__image-container">
          <video
            v-if="isMarketingVideo"
            :ref="setMarketingVideoRef"
            :src="resolvedMarketingVideoUrl"
            class="product-list-card__video"
            controls
            controlslist="nodownload"
            muted
            playsinline
            webkit-playsinline
            x5-video-player-type="h5"
            preload="metadata"
            @click.stop
            @contextmenu.prevent
            @play="handleVideoPlay"
            @pause="handleVideoPause"
            @seeking="handleVideoSeeking"
            @seeked="handleVideoSeeked"
            @ended="handleVideoEnded"
          ></video>
          <BaseImage
            v-else
            :src="getOssUrl(image)"
            :alt="modelNo"
            class="product-list-card__image"
          />
          <slot name="media-overlay" />
          <!-- Compare Button Overlay：仅满员禁用时用 a-tooltip 包裹，避免 !isDisabled 时 trigger 包装元素产生空白 -->
          <a-tooltip
            v-if="showCompare && isDisabled"
            content="最多比较4个产品，移除一个产品以添加另外一个产品"
            position="top"
          >
            <div
              class="product-list-card__compare product-list-card__compare--disabled"
              @click.stop.prevent="handleCompareToggle"
            >
              <div class="product-list-card__compare-icon">
                <SvgIcon title="产品对比" name="pk" size="14" filled />
              </div>
              <span class="product-list-card__compare-text">产品对比</span>
            </div>
          </a-tooltip>
          <div
            v-else-if="showCompare"
            class="product-list-card__compare"
            :class="{ 'product-list-card__compare--active': isInCompare }"
            @click.stop.prevent="handleCompareToggle"
          >
            <div class="product-list-card__compare-icon">
              <SvgIcon v-if="isInCompare" title="产品对比" name="remove-card" size="14" />
              <SvgIcon v-else title="产品对比" name="pk" size="14" filled />
            </div>
            <span class="product-list-card__compare-text">产品对比</span>
          </div>
        </div>
      </div>

      <div class="product-list-card__content">
        <div class="product-list-card__info">
          <div
            class="product-list-card__title-row"
            :class="{ 'product-list-card__title-row--with-tag': hasActivityTag }"
          >
            <template v-if="hasActivityTag">
              <BaseStoreActivityTag
                v-for="tag in activityTags"
                :key="tag"
                class="product-list-card__activity-tag"
              >
                {{ tag }}
              </BaseStoreActivityTag>
            </template>
            <BaseOverflowTooltip :content="name" tag="h3" class="product-list-card__title">
              {{ name }}
            </BaseOverflowTooltip>
          </div>
          <BaseOverflowTooltip :content="modelNo" tag="p" class="product-list-card__model">
            {{ modelNo }}
          </BaseOverflowTooltip>
          <div v-if="hasTierPriceTags" class="product-list-card__tier-tags">
            <span
              v-for="item in visibleTierPriceTags"
              :key="item.label"
              class="product-list-card__tier"
            >
              <span
                v-for="(part, partIndex) in item.label.split('|')"
                :key="`${item.label}-${partIndex}`"
                class="product-list-card__tier-part"
              >
                {{ part.trim() }}
              </span>
            </span>
            <a-popover
              v-if="hiddenTierPriceTags.length > 0 && !isMobile"
              :trigger="tierPopoverTrigger"
              position="br"
              :show-arrow="false"
              :arrow-style="{ display: 'none' }"
              content-class="product-list-card__tier-popover"
              :content-style="{
                padding: '12px',
                border: 'none',
                borderRadius: '8px',
              }"
            >
              <span
                class="product-list-card__tier product-list-card__tier--overflow"
                role="button"
                tabindex="0"
                @click.stop.prevent="expandTierPriceTags"
                @keydown.enter.stop.prevent="expandTierPriceTags"
                @keydown.space.stop.prevent="expandTierPriceTags"
              >
                +{{ tierPriceOverflowCount }}
              </span>
              <template #content>
                <div class="product-list-card__tier-popover-content" @click.stop.prevent>
                  <span
                    v-for="item in hiddenTierPriceTags"
                    :key="item.label"
                    class="product-list-card__tier-popover-item"
                  >
                    {{ item.label }}
                  </span>
                </div>
              </template>
            </a-popover>
            <span
              v-else-if="hiddenTierPriceTags.length > 0 && isMobile"
              class="product-list-card__tier product-list-card__tier--overflow"
              role="button"
              tabindex="0"
              @click.stop.prevent="expandTierPriceTags"
              @keydown.enter.stop.prevent="expandTierPriceTags"
              @keydown.space.stop.prevent="expandTierPriceTags"
            >
              +{{ tierPriceOverflowCount }}
            </span>
            <span
              v-if="canCollapseTierPriceTags"
              class="product-list-card__tier product-list-card__tier--collapse"
              role="button"
              tabindex="0"
              @click.stop.prevent="collapseTierPriceTags"
              @keydown.enter.stop.prevent="collapseTierPriceTags"
              @keydown.space.stop.prevent="collapseTierPriceTags"
            >
              收起
              <IconUp class="product-list-card__tier-action-icon" />
            </span>
          </div>
          <BaseOverflowTooltip
            v-if="!shouldHideBriefInCard"
            :content="description"
            tag="p"
            :line="props.subtitleLine"
            class="product-list-card__subtitle"
            :class="{ 'product-list-card__subtitle--single-line': props.subtitleLine === 1 }"
          >
            {{ description }}
          </BaseOverflowTooltip>
        </div>

        <div class="product-list-card__bottom">
          <div
            class="product-list-card__price-row"
            :class="{
              'product-list-card__price-row--delisted':
                isDelisted || isSoldOut || isInventoryUpdating,
            }"
          >
            <div class="product-list-card__price-group">
              <div class="product-list-card__price">
                <template v-if="campaignPriceDisplay">
                  <span
                    v-if="campaignPriceDisplay.pricePrefix"
                    class="product-list-card__price-prefix"
                  >
                    {{ campaignPriceDisplay.pricePrefix }}
                  </span>
                  <span class="currency">{{ campaignPriceDisplay.currencySymbol || '¥' }}</span>
                  <template v-if="campaignPriceParts">
                    <span class="integer">{{ campaignPriceParts.integer }}</span>
                    <span v-if="campaignPriceParts.decimal" class="decimal">
                      {{ campaignPriceParts.decimal }}
                    </span>
                  </template>
                  <span v-else class="integer">{{ campaignPriceDisplay.price }}</span>
                  <span
                    v-if="campaignPriceDisplay.priceSuffix"
                    class="product-list-card__price-suffix"
                  >
                    {{ campaignPriceDisplay.priceSuffix.replace(/\/\s+/g, '/') }}
                  </span>
                </template>
                <template v-else-if="displayPriceParts">
                  <span class="currency">¥</span>
                  <span class="integer">{{ displayPriceParts.integer }}</span>
                  <span v-if="displayPriceParts.decimal" class="decimal">
                    {{ displayPriceParts.decimal }}
                  </span>
                </template>
                <span v-else class="amount">¥ {{ compactEffectiveDisplayPrice }}</span>
              </div>
              <div v-if="shouldShowOriginalPrice" class="product-list-card__original-price">
                <template v-if="originalPriceParts">
                  <span class="currency">¥</span>
                  <span class="integer">{{ originalPriceParts.integer }}</span>
                  <span v-if="originalPriceParts.decimal" class="decimal">
                    {{ originalPriceParts.decimal }}
                  </span>
                </template>
                <span v-else class="amount">¥ {{ compactOriginalPrice }}</span>
              </div>
            </div>

            <button
              v-if="campaignActionLabel"
              type="button"
              class="product-list-card__status-button"
              :class="{ 'product-list-card__status-button--not-started': isCampaignNotStarted }"
              disabled
            >
              {{ campaignActionLabel }}
            </button>
            <slot
              v-else
              name="price-action"
              :product="props.product"
              :is-unavailable="isUnavailable"
            >
              <button
                v-if="effectiveShowCart || (props.showCart && (isSoldOut || isInventoryUpdating))"
                class="cart-button"
                :class="{
                  'cart-button--unavailable': isUnavailable,
                }"
                :aria-label="isUnavailable ? unavailableLabel : '加入购物车'"
                :disabled="isUnavailable"
                @click.stop.prevent="handleAddToCart"
              >
                <span v-if="isUnavailable" class="cart-text">{{ unavailableLabel }}</span>
                <SvgIcon
                  v-else
                  title="加入购物车"
                  name="add-shopping-cart"
                  class="cart-icon"
                  size="16"
                />
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style lang="scss" scoped>
.product-list-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: $bg-white;
  border-radius: $radius-md;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  padding: 16px;
  position: relative;

  @include respond-to-max('lg') {
    padding: $spacing-sm;
  }

  &__image-container {
    width: 200px;
    aspect-ratio: 1 / 1;
    border-radius: 8px;
    overflow: hidden;
    margin: 0 auto;
  }

  &:hover {
    box-shadow: $shadow-dropdown-level1;
  }

  &:hover .product-list-card__image {
    transform: scale($card-image-hover-scale);
  }

  &:hover .product-list-card__compare {
    opacity: 1;
  }

  &:hover .cart-button {
    opacity: 1;
  }

  &__countdown {
    position: absolute;
    top: 0;
    left: 50%;
    z-index: 4;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-2xs;
    height: 24px;
    padding: 0 $spacing-xs;
    border: 1px solid $bg-white;
    border-top: none;
    border-radius: 0 0 8px 8px;
    background-color: $primary-color;
    color: $bg-white;
    font-size: $font-size-caption;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;

    &--warning {
      background-color: $campaign-countdown-upcoming-bg;
    }
  }
}

.product-list-card__inner {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.product-list-card {
  &__media {
    position: relative;
  }

  &__image-container {
    aspect-ratio: 1 / 1;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    position: relative;
  }

  &__actions {
    position: absolute;
    top: $spacing-md;
    right: $spacing-md;
    z-index: 10;
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    pointer-events: auto;
  }

  &:hover &__actions {
    opacity: 1;
  }

  &__image-link {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    flex: 1;

    &:focus-visible {
      outline: 2px solid $primary-color;
      outline-offset: -2px;
      border-radius: 8px;
    }
  }

  &__compare {
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 98px;
    height: 30px;
    background-color: $bg-color;
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-xs;
    color: $primary-color;
    font-size: 14px;
    opacity: 0;
    transition: all 0.3s ease;
    cursor: pointer;
    z-index: 2;

    &-icon {
      width: 14px;
      height: 14px;
      color: $primary-color;
      flex-shrink: 0;
    }

    &--active {
      opacity: 1 !important;
      transform: translateX(-50%) !important;
      background-color: $bg-color;
      color: $text-content-color;

      .product-list-card__compare-icon {
        color: $text-content-color;
      }
    }

    &--disabled {
      cursor: not-allowed;
      background-color: $bg-color;
      color: $text-content-color;

      .product-list-card__compare-icon {
        color: $text-content-color;
      }
    }
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.5s ease;
  }

  &__video {
    width: 100%;
    height: 100%;
    display: block;
    background: $color-black;
    object-fit: contain;
  }

  &__content {
    flex: 1;
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    user-select: text;
    transition: transform 0.3s ease;
  }

  &__info {
    margin-bottom: 18px;
  }

  &__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 4px;
    width: 100%;
    min-width: 0;
    margin-bottom: 4px;
  }

  :deep(.product-list-card__activity-tag) {
    flex: 0 0 auto;
    margin: 3px 0 0;
    border: none;
    background-color: $auxiliary-color-1;
    color: $primary-color;
  }

  :deep(.product-list-card__title) {
    flex: 1;
    min-width: 0;
    color: $text-title-color;
    margin-bottom: 0;
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
  }

  :deep(.product-list-card__model) {
    font-size: $font-size-card-content;
    margin-bottom: 8px;
    color: $text-subtitle-color;
    line-height: 22px;
  }

  :deep(.product-list-card__subtitle) {
    font-size: 14px;
    line-height: 22px;
    min-height: 44px;
    color: $text-content-color;
  }

  &__tier-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    min-height: 20px;
    margin-top: 0;
    overflow: visible;
  }

  &__tier {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    max-width: 100%;
    padding: 0 $spacing-2xs;
    border-radius: 4px;
    background-color: $auxiliary-color-1;
    color: $primary-color;
    font-size: 12px;
    line-height: 18px;
    white-space: nowrap;
    flex-shrink: 0;

    &--overflow {
      cursor: pointer;
    }

    &--collapse {
      gap: 2px;
      cursor: pointer;
    }
  }

  &__tier-part {
    display: inline-flex;
    align-items: center;

    & + & {
      position: relative;
      margin-left: $spacing-xs;

      &::before {
        content: '';
        position: absolute;
        left: -$spacing-2xs;
        top: 50%;
        width: 10px;
        height: 1px;
        background-color: rgba($primary-color, 0.3);
        transform: translate(-50%, -50%) rotate(90deg);
      }
    }
  }

  &__tier-action-icon {
    width: 8px;
    height: 8px;
    font-size: 8px;
    line-height: 1;
    color: currentColor;
    flex-shrink: 0;
  }

  &__tier-popover-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: max-content;
  }

  &__tier-popover-item {
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    padding: 0 4px;
    border-radius: 4px;
    background-color: $auxiliary-color-1;
    color: $primary-color;
    font-size: 12px;
    line-height: 20px;
    white-space: nowrap;
  }

  :deep(.product-list-card__subtitle--single-line) {
    min-height: 22px;
  }

  &__bottom {
    margin-top: auto;
  }

  &__price-row {
    position: relative;
    padding-right: 52px;
    display: flex;
    align-items: center;

    &--delisted {
      padding-right: 100px;
    }
  }

  &__price-group {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: $spacing-xs;
    min-width: 0;
  }

  &__original-price {
    display: inline-flex;
    align-items: baseline;
    color: $text-stat-zero-color;
    font-family: $font-family-lato-numeric;
    font-weight: 400;
    text-decoration: line-through;
    white-space: nowrap;

    .amount {
      color: $text-stat-zero-color;
      font-size: 14px;
      line-height: 22px;
      font-weight: 400;
    }

    .currency {
      margin-right: 2px;
      color: $text-stat-zero-color;
      font-size: 12px;
      line-height: 1.4;
      font-weight: 400;
    }

    .integer {
      color: $text-stat-zero-color;
      font-size: 16px;
      line-height: 1.4;
      font-weight: 400;
    }

    .decimal {
      color: $text-stat-zero-color;
      font-size: 12px;
      line-height: 1.4;
      font-weight: 400;
    }
  }

  &__price {
    display: inline-flex;
    align-items: baseline;
    color: $text-title-color;
    font-family: $font-family-lato-numeric;

    .amount {
      color: $text-title-color;
      font-size: 18px;
      line-height: 26px;
      font-weight: 500;
    }

    .currency {
      margin-right: 2px;
      color: $text-title-color;
      font-size: 14px;
      line-height: 1.4;
      font-weight: 500;
    }

    .integer {
      color: $text-title-color;
      font-size: 20px;
      line-height: 1.4;
      font-weight: 500;
    }

    .decimal {
      color: $text-title-color;
      font-size: 14px;
      line-height: 1.4;
      font-weight: 500;
    }
  }

  &__price-prefix,
  &__price-suffix {
    color: $text-title-color;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }

  .cart-button {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    padding: 0;
    border-radius: $seckill-banner-radius;
    background-color: $primary-color;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    appearance: none;
    -webkit-appearance: none;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 0;
    z-index: 2;
    line-height: 0;
    flex-shrink: 0;
    overflow: hidden;

    &:hover {
      background-color: color-mix(in srgb, $primary-color, black 5%);
      transform: scale(1.1) !important;
    }

    &:active {
      transform: scale(0.95) !important;
    }

    .cart-icon {
      color: $bg-white;
      width: 16px;
      height: 16px;
      display: block;
      flex-shrink: 0;
    }

    .cart-text {
      color: $text-title-color;
      font-size: $font-size-card-content;
      line-height: 20px;
      white-space: nowrap;
    }

    &--unavailable {
      width: auto;
      min-width: 80px;
      padding: 0 12px;
      background-color: $bg-color;
      cursor: not-allowed;
      opacity: 0;

      &:hover,
      &:active {
        background-color: $bg-color;
        transform: scale(1) !important;
      }
    }
  }

  &__status-button {
    position: absolute;
    right: 0;
    bottom: 0;
    height: 32px;
    min-width: 58px;
    padding: 0 $spacing-xs;
    border: none;
    border-radius: $seckill-banner-radius;
    background-color: $bg-color;
    color: $text-subtitle-color;
    font-family: $font-family-lato-numeric;
    font-size: $font-size-card-content;
    font-weight: 400;
    line-height: $line-height-title-2-5;
    white-space: nowrap;
    cursor: not-allowed;
    box-shadow: 0 1px 2px 0 rgba($text-title-color, 0);

    &--not-started {
      opacity: 1;
      transform: none;
    }
  }
}

@include respond-to-max('lg') {
  .product-list-card {
    padding: 12px;
    min-width: 0;
    overflow: hidden;
    box-shadow: none;

    &:hover {
      box-shadow: none;
    }

    &:hover .product-list-card__image {
      transform: none;
    }

    &__compare {
      display: none;
    }

    &__inner {
      min-width: 0;
    }

    &__content {
      padding-top: $spacing-sm;
      min-width: 0;
    }

    &__info {
      min-width: 0;
      margin-bottom: 12px;
      overflow: hidden;
    }

    :deep(.product-list-card__title) {
      font-size: 14px;
      line-height: 22px;
    }

    &__title-row {
      margin-bottom: 4px;
    }

    :deep(.product-list-card__activity-tag) {
      margin-top: 1px;
    }

    :deep(.product-list-card__model) {
      font-size: 12px;
      margin-bottom: 8px;
      line-height: 18px;
    }

    &.product-list-card--campaign :deep(.product-list-card__model) {
      margin-bottom: 4px;
    }

    :deep(.product-list-card__subtitle) {
      font-size: 12px;
      line-height: 18px;
    }

    :deep(.product-list-card__subtitle--single-line) {
      min-height: 18px;
    }

    &__price {
      min-width: 0;
      flex-direction: row;
      align-items: baseline;
      gap: 0;

      .amount {
        line-height: 22px;
        white-space: nowrap;
      }

      .currency {
        margin-right: 2px;
        font-size: 12px;
        line-height: 22px;
        font-weight: 600;
      }

      .integer {
        font-size: 16px;
        line-height: 1.4;
        font-weight: 600;
      }

      .decimal {
        font-size: 12px;
        line-height: 1.4;
        font-weight: 600;
      }
    }

    &__original-price {
      font-weight: 400;

      .amount {
        font-size: 12px;
        line-height: 20px;
        font-weight: 400;
        white-space: nowrap;
      }

      .currency {
        margin-right: 2px;
        font-size: 10px;
        line-height: 20px;
        font-weight: 400;
      }

      .integer {
        font-size: 14px;
        line-height: 1.4;
        font-weight: 400;
      }

      .decimal {
        font-size: 10px;
        line-height: 1.4;
        font-weight: 400;
      }
    }

    &__price-row {
      padding-right: 34px;
      min-height: 26px;
      min-width: 0;
      overflow: visible;
      position: relative;
      display: flex;
      align-items: flex-end;

      &--delisted {
        padding-right: 80px;
      }
    }

    .cart-button {
      position: absolute;
      right: 0;
      bottom: 0;
      top: auto;
      transform: none;
      width: 26px;
      height: 26px;
      min-width: 26px;
      min-height: 26px;
      opacity: 1;
      border-radius: 6px;

      .cart-icon {
        width: 14px;
        height: 14px;
      }

      &:hover {
        transform: scale(1.1) !important;
      }

      &:active {
        transform: scale(0.95) !important;
      }
    }

    .cart-button--unavailable {
      min-width: 72px;
      width: auto;
      padding: 0 10px;
      transform: none;

      &:hover,
      &:active,
      &:disabled {
        transform: none !important;
      }
    }
  }

  .product-list-card--campaign {
    .product-list-card__tier-tags {
      flex-wrap: nowrap;
      align-items: center;
      gap: 4px;
      min-height: 18px;
      max-height: 18px;
      overflow: hidden;
    }

    &.product-list-card--tier-expanded .product-list-card__tier-tags {
      flex-wrap: wrap;
      align-items: flex-start;
      max-height: none;
      overflow: visible;
    }

    .product-list-card__tier {
      height: 18px;
      padding: 0 4px;
      border-radius: 4px;
      background-color: $auxiliary-color-1;
      color: $primary-color;
      font-size: 10px;
      line-height: 18px;
    }

    .product-list-card__tier-part + .product-list-card__tier-part {
      margin-left: 8px;

      &::before {
        left: -4px;
        width: 8px;
        background-color: rgba($primary-color, 0.35);
      }
    }

    .product-list-card__tier--overflow {
      gap: 2px;
      cursor: pointer;
      flex-shrink: 0;
      background-color: $auxiliary-color-1;
      color: $primary-color;

      &::after {
        content: '';
        display: inline-block;
        width: 4px;
        height: 4px;
        border-top: 1px solid currentColor;
        border-right: 1px solid currentColor;
        transform: rotate(45deg);
        flex-shrink: 0;
      }
    }

    .product-list-card__tier--collapse {
      gap: 2px;
      cursor: pointer;
      flex-shrink: 0;
    }

    .product-list-card__countdown {
      top: 0;
      left: 0;
      height: 20px;
      padding: 0 8px;
      transform: none;
      border: 0.5px solid $bg-white;
      border-radius: 12px 0 8px 0;
      gap: 4px;
      font-size: 11px;
      line-height: 14px;

      &--warning {
        background-color: $campaign-countdown-upcoming-bg;
      }
    }

    .product-list-card__status-button {
      height: 32px;
      min-width: 58px;
      padding: 0 $spacing-xs;
      border-radius: $seckill-banner-radius;
      background-color: $bg-color;
      color: $text-subtitle-color;
      font-family: $font-family-lato-numeric;
      font-size: $font-size-card-content;
      font-weight: 400;
      line-height: $line-height-title-2-5;
      box-shadow: 0 1px 2px 0 rgba($text-title-color, 0);
      opacity: 1;
      transform: none;
    }

    .product-list-card__price {
      color: $primary-color;

      .currency,
      .integer,
      .decimal {
        color: $primary-color;
      }
    }

    .product-list-card__price-prefix,
    .product-list-card__price-suffix {
      color: $primary-color;
    }
  }
}

:global(.product-list-card__tier-popover) {
  box-shadow: $shadow-dropdown-level1;
}

:global(.product-list-card__tier-popover .arco-popover-content) {
  padding: 0;
}
</style>
